import React, { useState, useEffect, useCallback } from 'react'
import { Button, CircularProgress } from '@material-ui/core'
import Input from 'components/forms/Input'
import { Switch } from 'components/ds/Choice'
import { useDispatch, useSelector } from 'react-redux'
import request from 'tools/request'
import { dispatchApplicantById } from 'actions/applicants'
import Moment from "react-moment"
import AuthService from 'tools/AuthService'
import emptyIcon from 'styles/images/icons/empty.svg'
import './notes.scss'

const NoDataIndication = () => (
    <div className='noDataAvailable'>
        <img src={emptyIcon} alt='empty' />
        <p>No Notes Available.</p>
    </div>
)

const NotesList = ({ data }) => {
    const {
        notes,
        updatedAt,
        createdBy,
        createdFromHubSpotSync
    } = data
    const splitName = createdBy?.name?.split(' ') || []
    const avatarName = splitName.length >= 2 ? splitName[0][0] + splitName[1][0] : (createdBy?.name?.[0] || '?')
    return (
        <div className='notesListContainer'>
            <div className='userName'>
                {avatarName}
            </div>
            <div className='notesContent'>
                <div className='title'>
                    <p>{createdBy?.name}</p>
                    {createdFromHubSpotSync && (
                        <span className='hubspotNoteBadge'>Created from HubSpot</span>
                    )}
                </div>
                <div>
                    <p>{notes}</p>
                </div>
                <div className='createdDate'>
                    <Moment format="DD-MM-YYYY">{updatedAt}</Moment>
                </div>
            </div>
        </div>
    )
}

const Notes = (props) => {
    const {
        createNotes,
        brokerCreateNotes,
        getNotes,
        brokerGetNotes,
        applicantId,
        notesList,
        isBrokerCustomer,
        salesRepresentative
    } = props
    const [loading, setLoading] = useState(false)
    const [notes, setNotes] = useState('')
    const dispatch = useDispatch()
    const salesRepNoteEmails = useSelector(state => state?.applicant?.applicantById?.salesRepNoteEmails)
    const [savingSalesRepEmails, setSavingSalesRepEmails] = useState(false)
    const salesRepName = [salesRepresentative?.firstName, salesRepresentative?.lastName].filter(Boolean).join(' ')
    const canNotifySalesRep = AuthService.getClientFeatures().salesRepNoteEmailsEnabled
        && !isBrokerCustomer && !AuthService.isReadOnlyUser() && !!salesRepresentative?.email

    const handleSalesRepEmailsChange = (enabled) => {
        setSavingSalesRepEmails(true)
        request(`applications/${applicantId}/sales-rep-note-emails`, 'PATCH', { enabled })
            .then(res => {
                if (res && typeof res.enabled === 'boolean') {
                    dispatch((innerDispatch, getState) => {
                        const current = getState()?.applicant?.applicantById
                        if (current?.uuid === applicantId) {
                            innerDispatch(dispatchApplicantById({ ...current, salesRepNoteEmails: res.enabled }))
                        }
                    })
                }
            })
            .finally(() => setSavingSalesRepEmails(false))
    }

    const getNotesList = useCallback(() => {
        setLoading(true)
        if (isBrokerCustomer){
            brokerGetNotes(applicantId).then(() => {
                setLoading(false)
            })
        } else {
            getNotes(applicantId).then(() => {
                setLoading(false)
            })
        }
    }, [applicantId, getNotes, brokerGetNotes])

    useEffect(() => {
        getNotesList()
    }, [getNotesList])

    const handleChange = (e) => {
        setNotes(e.target.value)
    }

    const handleSubmit = () => {
        if (isBrokerCustomer){
            brokerCreateNotes(applicantId, { notes: notes }).finally(() => {
                getNotesList()
                setNotes('')
            })
        } else {
            createNotes(applicantId, { notes: notes }).finally(() => {
                getNotesList()
                setNotes('')
            })
        }
    }

    return (
        <div className='notesContainer'>
            <p>Notes</p>
            {canNotifySalesRep && (
                <div className='notifySalesRep'>
                    <Switch
                        label={`Email new notes to ${salesRepName || salesRepresentative.email} (sales rep)`}
                        checked={!!salesRepNoteEmails}
                        disabled={savingSalesRepEmails}
                        onChange={(e) => handleSalesRepEmailsChange(e.target.checked)}
                    />
                </div>
            )}
            {!AuthService.isReadOnlyUser() &&
                <div className='fieldContainer'>
                    <Input
                        name='notes'
                        value={notes}
                        placeholder='Enter Your Notes Here'
                        multiline={true}
                        className='notesInput'
                        onChange={(e) => handleChange(e)}
                    />
                    <div className='btnContainer'>
                        <Button
                            variant='contained'
                            color='primary'
                            onClick={() => handleSubmit()}
                            disabled={loading || notes.length === 0}
                        >
                            Add Notes
                        </Button>
                    </div>
                </div>}
            {
                loading ? <CircularProgress className='circularLoader' /> : (
                    notesList.map((item) => (
                        <NotesList
                            data={item}
                        />
                    ))
                )
            }
            {
                (!loading && notesList?.length === 0) && (
                    <NoDataIndication />
                )
            }
        </div>
    )
}

export default Notes